import React, { useState } from 'react';
import ContactForm from './ContactForm';
import TeamForm from './TeamForm';
import { getRelationColor } from './relationStyles';
import './Contacts.css';

const ContactList = ({ 
  contacts, 
  teams = [],
  onSelectContact, 
  onSelectTeam,
  onAddContact, 
  onEditContact, 
  onDeleteContact,
  onAddTeam,
  onEditTeam,
  onDeleteTeam,
  onAddTeamMember,
  onRemoveTeamMember,
  selectedContactId,
  selectedTeamId,
  loading,
  loadingTeams,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('contacts');
  const [showContactForm, setShowContactForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);

  // Contact handlers
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactForm(true);
  };

  const handleEditContact = (contact, e) => {
    e.stopPropagation();
    setEditingContact(contact);
    setShowContactForm(true);
  };

  const handleSaveContact = async (contactData) => {
    if (editingContact) {
      await onEditContact(editingContact.id, contactData);
    } else {
      await onAddContact(contactData);
    }
    setShowContactForm(false);
    setEditingContact(null);
  };

  const handleDeleteContactClick = (id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this contact?')) {
      onDeleteContact(id);
    }
  };

  // Team handlers
  const handleAddTeam = () => {
    setEditingTeam(null);
    setShowTeamForm(true);
  };

  const handleEditTeam = (team, e) => {
    e.stopPropagation();
    setEditingTeam(team);
    setShowTeamForm(true);
  };

  const handleSaveTeam = async (teamData) => {
    if (editingTeam) {
      await onEditTeam(editingTeam.id, teamData);
    } else {
      await onAddTeam(teamData);
    }
    setShowTeamForm(false);
    setEditingTeam(null);
  };

  const handleDeleteTeamClick = (id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this team?')) {
      onDeleteTeam(id);
    }
  };

  const formatDOB = (dob) => {
    if (!dob) return '';
    return new Date(dob).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`contacts-panel ${isOpen ? 'open' : ''}`}>
      {/* Tabs */}
      <div className="contacts-tabs">
        <button 
          className={`contacts-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>Contacts</span>
        </button>
        <button 
          className={`contacts-tab ${activeTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveTab('teams')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span>Teams</span>
        </button>
      </div>

      {/* Contacts Tab Content */}
      {activeTab === 'contacts' && (
        <>
          {/* Header */}
          <div className="contacts-header">
            <h3>Contacts</h3>
            <button className="contacts-add-btn" onClick={handleAddContact}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>

          {/* Anonymous Chat Option - Moved to top */}
          <div className="contacts-anonymous contacts-anonymous-top">
            <button 
              className={`anonymous-chat-btn ${!selectedContactId && !selectedTeamId ? 'active' : ''}`}
              onClick={() => onSelectContact(null)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>Chat without selecting anyone</span>
            </button>
          </div>

          {/* Description */}
          <p className="contacts-description">
            Select a contact to start a chat about them
          </p>

          {/* Contact List */}
          <div className="contacts-list">
            {loading ? (
              <div className="contacts-loading">Loading contacts...</div>
            ) : contacts.length === 0 ? (
              <div className="contacts-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <p>No contacts yet</p>
                <button onClick={handleAddContact}>Add your first contact</button>
              </div>
            ) : (
              contacts.map(contact => (
                <div 
                  key={contact.id}
                  className={`contact-item ${selectedContactId === contact.id ? 'selected' : ''}`}
                  onClick={() => onSelectContact(contact)}
                >
                  <div 
                    className="contact-avatar"
                    style={{ backgroundColor: getRelationColor(contact.relation) }}
                  >
                    {contact.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="contact-info">
                    <div className="contact-name">{contact.name}</div>
                    <div className="contact-details">
                      {contact.relation && (
                        <span 
                          className="contact-relation"
                          style={{ color: getRelationColor(contact.relation) }}
                        >
                          {contact.relation}
                        </span>
                      )}
                      {contact.dob && (
                        <span className="contact-dob">{formatDOB(contact.dob)}</span>
                      )}
                    </div>
                  </div>
                  <div className="contact-actions">
                    <button 
                      className="contact-edit-btn"
                      onClick={(e) => handleEditContact(contact, e)}
                      title="Edit"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button 
                      className="contact-delete-btn"
                      onClick={(e) => handleDeleteContactClick(contact.id, e)}
                      title="Delete"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Teams Tab Content */}
      {activeTab === 'teams' && (
        <>
          {/* Header */}
          <div className="contacts-header">
            <h3>Teams</h3>
            <button className="contacts-add-btn" onClick={handleAddTeam}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>

          {/* Description */}
          <p className="contacts-description">
            Analyze team dynamics and compatibility
          </p>

          {/* Team List */}
          <div className="contacts-list">
            {loadingTeams ? (
              <div className="contacts-loading">Loading teams...</div>
            ) : teams.length === 0 ? (
              <div className="contacts-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <p>No teams yet</p>
                <button onClick={handleAddTeam}>Create your first team</button>
              </div>
            ) : (
              teams.map(team => (
                <div 
                  key={team.id}
                  className={`contact-item team-item ${selectedTeamId === team.id ? 'selected' : ''}`}
                  onClick={() => onSelectTeam(team)}
                >
                  <div className="team-avatar">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <div className="contact-info">
                    <div className="contact-name">{team.name}</div>
                    <div className="contact-details">
                      <span className="team-member-count">
                        {team.member_count || 0} member{(team.member_count || 0) !== 1 ? 's' : ''}
                      </span>
                      {team.description && (
                        <span className="team-description">{team.description}</span>
                      )}
                    </div>
                    {/* Member preview avatars */}
                    {team.members_preview && team.members_preview.length > 0 && (
                      <div className="team-members-preview">
                        {team.members_preview.slice(0, 4).map((member, idx) => (
                          <div 
                            key={member.id || idx} 
                            className="team-member-mini"
                            title={member.name}
                          >
                            {member.name?.charAt(0).toUpperCase()}
                          </div>
                        ))}
                        {team.members_preview.length > 4 && (
                          <div className="team-member-mini team-member-more">
                            +{team.members_preview.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="contact-actions">
                    <button 
                      className="contact-edit-btn"
                      onClick={(e) => handleEditTeam(team, e)}
                      title="Edit"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button 
                      className="contact-delete-btn"
                      onClick={(e) => handleDeleteTeamClick(team.id, e)}
                      title="Delete"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm
          contact={editingContact}
          onSave={handleSaveContact}
          onClose={() => {
            setShowContactForm(false);
            setEditingContact(null);
          }}
        />
      )}

      {/* Team Form Modal */}
      {showTeamForm && (
        <TeamForm
          team={editingTeam}
          contacts={contacts}
          onSave={handleSaveTeam}
          onClose={() => {
            setShowTeamForm(false);
            setEditingTeam(null);
          }}
        />
      )}
    </div>
  );
};

export default ContactList;
